import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, getUser, isStudent, isTeacher } from '../../utils/firebase';
import { useAuth } from '../../contexts/AuthContext';
import GoogleIcon from '../../components/icons/GoogleIcon';
import MicrosoftIcon from '../../components/icons/MicrosoftIcon';
import HideIcon from '../../components/icons/HideIcon';
import Button from '../../components/ui/Button';

type Role = 'student' | 'teacher';

interface LocationState {
  from?: { pathname: string };
}

const brandColor = '#FF6F0F';
const darkColor = '#293044';
const assistiveColor = '#989898';
const borderColor = '#F0F0F0';

const pxToRem = (value: number) => `${value / 16}rem`;

const Login: React.FC = () => {
  const { loginWithGoogle, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [tabValue, setTabValue] = useState<number>(0);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('loginTab');
    if (saved === 'teacher') {
      setTabValue(1);
    }
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const selectedRole: Role = useMemo(() => (tabValue === 0 ? 'student' : 'teacher'), [tabValue]);

  const handleRoleChange = (role: Role) => {
    const value = role === 'student' ? 0 : 1;
    setTabValue(value);
    localStorage.setItem('loginTab', role);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNavigateRegister = () => {
    navigate('/signupchoice', { state: { from: (location.state as LocationState)?.from } });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const credential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = credential.user;

      // 승인된 교사는 schools/{schoolId}/teachers/{uid} 등 하위 경로에만 있을 수 있음.
      // 루트 students/teachers 문서만 보면 이메일 가입+승인 사용자가 계속 '미가입'으로 처리됨.
      const profile = await getUser(user.uid);
      if (!profile) {
        navigate('/signupchoice', {
          state: {
            ...(location.state as LocationState),
            email: formData.email,
            isSocialLogin: false,
            suggestedRole: selectedRole,
          },
        });
        return;
      }
      if (isStudent(profile)) {
        navigate('/class/student');
        return;
      }
      if (isTeacher(profile)) {
        navigate('/class/teacher/ClassTeacherHome');
        return;
      }
      navigate('/signupchoice', {
        state: {
          ...(location.state as LocationState),
          email: formData.email,
          isSocialLogin: false,
          suggestedRole: selectedRole,
        },
      });
    } catch (submitError: any) {
      console.error('로그인 오류:', submitError);
      setError(submitError.message || '로그인에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'microsoft') => {
    setError('');
    setLoading(true);

    try {
      console.log('소셜 로그인 버튼 클릭:', provider, 'selectedRole:', selectedRole);
      if (provider === 'google') {
        await loginWithGoogle(selectedRole);
      } else {
        await loginWithMicrosoft(selectedRole);
      }
      // redirect 방식 사용 시 여기까지 오지 않음 (페이지가 이동함)
      console.log('소셜 로그인 완료 (팝업 방식)');
    } catch (socialError: any) {
      console.error('소셜 로그인 오류:', socialError);

      if (socialError.code === 'auth/unauthorized-domain') {
        setError('로그인 도메인이 허용되지 않았습니다.\nFirebase Console > Authentication > Settings > Authorized domains에서 도메인을 추가해주세요.');
      } else if (socialError.code === 'auth/account-exists-with-different-credential') {
        setError('이미 다른 로그인 방식으로 등록된 계정입니다.\n이메일/비밀번호로 로그인하시거나, 다른 소셜 로그인 방식을 시도해주세요.');
      } else if (socialError.code === 'auth/popup-closed-by-user') {
        setError('로그인 창이 닫혔습니다. 다시 시도해주세요.');
      } else if (socialError.code === 'auth/cancelled-popup-request') {
        setError('로그인이 취소되었습니다. 다시 시도해주세요.');
      } else if (socialError.message === 'SOCIAL_SIGNUP_REQUIRED') {
        navigate('/signupchoice', {
          state: {
            ...(location.state as LocationState),
            isSocialSignup: true,
            provider,
          },
        });
      } else {
        setError(socialError.message || '소셜 로그인에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const columnMaxWidth = isMobile ? '100%' : pxToRem(410);
  const fieldWidth = isMobile ? '100%' : pxToRem(354);
  const cardPadding = isMobile
    ? `${pxToRem(20)} ${pxToRem(16)}`
    : `${pxToRem(24)} ${pxToRem(28)}`;
  const cardGap = isMobile ? pxToRem(24) : pxToRem(40);
  const formGap = isMobile ? pxToRem(16) : pxToRem(24);

  const renderTabButton = (role: Role, label: string) => {
    const isSelected = selectedRole === role;
  return (
      <button
        type="button"
        onClick={() => handleRoleChange(role)}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: pxToRem(4),
          padding: `${pxToRem(6)} ${pxToRem(12)}`,
          minHeight: pxToRem(44),
          WebkitTapHighlightColor: 'transparent',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: isSelected ? brandColor : darkColor,
          fontSize: pxToRem(16),
          fontWeight: 500,
          transition: 'color 0.2s'
        }}
      >
        <span>{label}</span>
        <span
          style={{
            display: 'block',
            width: pxToRem(18),
            height: pxToRem(2),
            borderRadius: pxToRem(2),
            background: isSelected ? brandColor : 'transparent',
            transition: 'background 0.2s'
          }}
        />
      </button>
    );
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: isMobile ? 'calc(100dvh - 60px)' : 0,
        padding: isMobile
          ? `24px 16px calc(24px + env(safe-area-inset-bottom, 0px))`
          : '60px 24px 90px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)',
        boxSizing: 'border-box',
        overflow: isMobile ? 'auto' : 'hidden',
        overflowX: 'hidden'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: columnMaxWidth,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: pxToRem(16)
        }}
      >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: pxToRem(20)
                }}
              >
                <h1
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontSize: pxToRem(24),
                    fontWeight: 500,
                    color: '#000',
                    margin: 0
                  }}
                >
            로그인
                </h1>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: pxToRem(0)
                  }}
                >
                  <div
                    style={{
                      width: pxToRem(80),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {renderTabButton('student', '학생')}
                  </div>
                  <div
                    style={{
                      width: pxToRem(80),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {renderTabButton('teacher', '교사')}
                  </div>
                </div>
              </div>

              <div
                style={{
                  width: '100%',
                  background: '#F8F8F8',
                  borderRadius: isMobile ? pxToRem(16) : pxToRem(20),
                  padding: cardPadding,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: cardGap,
                  boxSizing: 'border-box'
                }}
              >
          {error && (
                  <div
                    style={{
                      width: '100%',
                      padding: `${pxToRem(12)} ${pxToRem(16)}`,
                      borderRadius: pxToRem(12),
                      background: 'rgba(255, 56, 56, 0.1)',
                      color: '#C33333',
                      fontSize: pxToRem(14),
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      whiteSpace: 'pre-line'
              }}
            >
              {error}
                  </div>
          )}

                <form
                  onSubmit={handleSubmit}
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: formGap
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      width: '100%'
                    }}
                  >
                    <div
                      style={{
                        width: fieldWidth,
                        maxWidth: '100%',
                        minHeight: pxToRem(52),
                        height: isMobile ? 'auto' : pxToRem(56),
                        background: '#FFFFFF',
                        borderRadius: pxToRem(12),
                        border: `${pxToRem(1.2)} solid ${borderColor}`,
                        padding: `${pxToRem(16)} ${pxToRem(20)}`,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <input
                        type="email"
                        name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                        placeholder="이메일"
                        autoComplete="email"
                        inputMode="email"
                        style={{
                          width: '100%',
                          border: 'none',
                          outline: 'none',
                          fontSize: pxToRem(16),
                          color: formData.email ? '#161616' : assistiveColor,
                          fontFamily: 'inherit',
                          background: 'transparent',
                          fontWeight: 400
                        }}
              />
                    </div>
                    <div
                      style={{
                        width: fieldWidth,
                        maxWidth: '100%',
                        minHeight: pxToRem(52),
                        height: isMobile ? 'auto' : pxToRem(56),
                        background: '#FFFFFF',
                        borderRadius: pxToRem(12),
                        border: `${pxToRem(1.2)} solid ${borderColor}`,
                        padding: `${pxToRem(16)} ${pxToRem(20)}`,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        gap: pxToRem(8)
                      }}
                    >
                      <input
                        type="password"
                        name="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                        placeholder="비밀번호"
                        autoComplete="current-password"
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          fontSize: pxToRem(16),
                          color: formData.password ? '#161616' : assistiveColor,
                          fontFamily: 'inherit',
                          background: 'transparent',
                          fontWeight: 400
                        }}
                      />
                      <HideIcon width={pxToRem(24)} height={pxToRem(24)} fill={assistiveColor} />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      width: '100%',
                      maxWidth: '100%'
                    }}
                  >
              <Button
                type="submit"
                      width={fieldWidth}
                      padding={`${pxToRem(16)} ${pxToRem(20)}`}
                      variant="primary"
                      size="large"
                disabled={loading}
              >
                      {loading ? '로그인 중...' : '로그인'}
              </Button>
              <Button
                      type="button"
                      width={fieldWidth}
                      padding={`${pxToRem(16)} ${pxToRem(20)}`}
                      variant="outline"
                      size="large"
                      onClick={handleNavigateRegister}
              >
                      회원가입
              </Button>
                  </div>
                </form>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: pxToRem(12)
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      color: assistiveColor,
                      fontSize: pxToRem(14),
                      fontWeight: 500
                    }}
                  >
                    다음 계정으로 계속하기
                  </div>

                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: pxToRem(8)
                    }}
              >
              <Button
                      variant="line"
                      size="large"
                      width="100%"
                      padding={`${pxToRem(16)} ${pxToRem(20)}`}
                      iconSize={18}
                      leftIcon={<GoogleIcon width={18} height={18} />}
                disabled={loading}
                      onClick={() => handleSocialLogin('google')}
              >
                      Google로 계속하기
              </Button>

            <Button
                      variant="line"
                      size="large"
                      width="100%"
                      padding={`${pxToRem(16)} ${pxToRem(20)}`}
                      iconSize={18}
                      leftIcon={<MicrosoftIcon width={18} height={18} />}
                      disabled={loading}
                      onClick={() => handleSocialLogin('microsoft')}
                    >
                      Microsoft로 계속하기
            </Button>
                  </div>
                </div>
              </div>
      </div>
    </div>
  );
};

export default Login; 